import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getTournamentById } from '../../services/tournamentService';
import { Tournament } from '../../services/tournamentService';
import MaintenanceRankings from '../../components/Rankings/MaintenanceRankings';
import { FaChevronLeft, FaSpinner, FaExclamationTriangle, FaUsers, FaUserFriends, FaTrophy } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';
import { getUserId } from '../../services/authService';
import '../Statistics/statistics.css';

const MaintenanceRankingPage: React.FC = () => {
  const { theme } = useTheme();
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState<Tournament | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [displayType, setDisplayType] = useState<'users' | 'teams'>('users');
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);

  useEffect(() => {
    const fetchTournament = async () => {
      if (!tournamentId) {
        setError('ID giải đấu không hợp lệ');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const tournamentData = await getTournamentById(parseInt(tournamentId));
        setTournament(tournamentData);

        // Lấy ID người dùng hiện tại
        const userId = getUserId();
        setCurrentUserId(userId);
      } catch (err: any) {
        setError(err.message || 'Có lỗi xảy ra khi tải thông tin giải đấu');
      } finally {
        setLoading(false);
      }
    };

    fetchTournament();
  }, [tournamentId]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleSwitchDisplayType = (type: 'users' | 'teams') => {
    setDisplayType(type);
  };

  if (loading) {
    return (
      <div className="loading-state">
        <FaSpinner className="icon-spin" size={40} color="#20757A" />
        <div>Đang tải dữ liệu giải đấu...</div>
      </div>
    );
  }

  if (error || !tournament) {
    return (
      <div className="error-message">
        <FaExclamationTriangle size={20} />
        <span>{error || 'Không tìm thấy giải đấu'}</span>
        <button className="btn-back" onClick={handleBack}>Quay lại</button>
      </div>
    );
  }

  // Hàm hỗ trợ định dạng ngày tháng
  const formatDate = (date: string | number[]): string => {
    if (Array.isArray(date)) {
      return new Date(date[0], date[1] - 1, date[2]).toLocaleDateString('vi-VN');
    }
    return new Date(date).toLocaleDateString('vi-VN');
  };

  return (
    <div className={`statistics-page ${theme}`}>
      <div className="statistics-header">
        <button className="btn-back" onClick={handleBack}>
          <FaChevronLeft /> Quay lại
        </button>
        <h1 className="statistics-title">
          <FaTrophy /> Bảng xếp hạng {tournament.name}
        </h1>
      </div>

      <div className="tournament-info">
        <div className="tournament-dates">
          Thời gian: {formatDate(tournament.startDate)} đến {formatDate(tournament.endDate)}
        </div>
        <div className="tournament-description">
          {tournament.description}
        </div>
      </div>

      <div className="display-type-toggle">
        <button 
          className={`toggle-btn ${displayType === 'users' ? 'active' : ''}`}
          onClick={() => handleSwitchDisplayType('users')}
        >
          <FaUsers /> Xếp hạng cá nhân
        </button>
        <button 
          className={`toggle-btn ${displayType === 'teams' ? 'active' : ''}`}
          onClick={() => handleSwitchDisplayType('teams')}
        >
          <FaUserFriends /> Xếp hạng đội
        </button>
      </div>

      {tournamentId && (
        <MaintenanceRankings
          tournamentId={parseInt(tournamentId)}
          userId={currentUserId || undefined}
          displayType={displayType}
          startDate={tournament.startDate instanceof Array ? 
            `${tournament.startDate[0]}-${String(tournament.startDate[1]).padStart(2, '0')}-${String(tournament.startDate[2]).padStart(2, '0')}` : 
            tournament.startDate}
          endDate={tournament.endDate instanceof Array ? 
            `${tournament.endDate[0]}-${String(tournament.endDate[1]).padStart(2, '0')}-${String(tournament.endDate[2]).padStart(2, '0')}` : 
            tournament.endDate}
        />
      )}
    </div>
  );
};

export default MaintenanceRankingPage; 